import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { actionNameBuilder } from "../utilities/storeActionNameBuilder";
import { applyResetMiddleware } from "./middleware/resetMiddleware";
import { useShallow } from "zustand/react/shallow";

const actionName = actionNameBuilder("popup");

export interface PopupCoords {
  lng: number;
  lat: number;
}

/**
 * Primary popup types — mutex (only one at a time, or null = closed).
 *
 * Note: "media-detail" (the MediaDrawer carousel) is NOT here.
 * It can coexist with any primary popup, so it lives independently
 * in mediaStore.isMediaDetailOpen (Layer B).
 */
export type PopupType = "geo-entity" | "non-geo-entity" | "media-viewer";

interface PopupState {
  /**
   * null  → no popup open
   * non-null → which primary popup type is active
   */
  popupType: PopupType | null;

  /**
   * Coords from the triggering event (map click).
   * null when opened from sidebar/URL — MapComponent derives
   * coords from entity data at render time in that case.
   */
  coords: PopupCoords | null;

  /**
   * Whether the popup panel content is currently visible.
   * false = panel is collapsed / "Switch to Map" mode.
   */
  isContentVisible: boolean;

  /**
   * Whether the popup content is in the minimised corner-card state.
   */
  isContentCollapsed: boolean;

  /**
   * Incremented when geo-entity popup content changes size (e.g. details expanded).
   * MapComponent listens and pans so the MapLibre popup stays inside the map container.
   */
  geoPopupLayoutRevision: number;

  /**
   * Whether the full-panel media viewer is open for a geotagged media item.
   * When true, the MapLibre geo popup is hidden and MediaEntityMapModal renders.
   * Closing the full viewer returns to the map geo popup (isFullViewerOpen → false).
   */
  isFullViewerOpen: boolean;

  actions: {
    /**
     * Open a geo-entity map popup.
     * Pass coords when known (map click). Omit when opening from sidebar/URL —
     * MapComponent will derive coords from the fetched entity.
     */
    openGeoPopup: (coords?: PopupCoords) => void;
    /** Non-geo entity side-panel popup */
    openNonGeoPopup: () => void;
    /** Media entity viewer popup — includes "Switch to Map" toggle */
    openMediaViewer: () => void;
    /** Open media viewer already in map mode (content collapsed) — avoids the brief flash of openMediaViewer + toggleContent */
    openMediaViewerInMapMode: () => void;
    /** Close the primary popup entirely */
    closePopup: () => void;
    /**
     * Toggle content visibility between map view and media/detail view.
     * Only meaningful for "media-viewer" popupType (the "Switch to Map" button).
     */
    toggleContent: () => void;
    /** Signal that geo popup DOM size may have changed (ResizeObserver / expand details). */
    bumpGeoPopupLayoutRevision: () => void;
    /** Open the full-panel media viewer for a geotagged media item (popup=media-full in URL). */
    openFullMediaViewer: () => void;
    /** Close the full-panel media viewer, returning to the geo popup (popup=media in URL). */
    closeFullMediaViewer: () => void;
    reset: () => void;
  };
}

const initialState = {
  popupType: null as PopupType | null,
  coords: null as PopupCoords | null,
  isContentVisible: true,
  isContentCollapsed: false,
  geoPopupLayoutRevision: 0,
  isFullViewerOpen: false
};

export const usePopupStore = create<PopupState>()(
  devtools(
    immer(
      applyResetMiddleware((set, _, store) => ({
        ...initialState,
        actions: {
          openGeoPopup: (coords) =>
            set(
              (state) => {
                state.popupType = "geo-entity";
                state.coords = coords ?? null;
                state.isContentVisible = true;
                state.isContentCollapsed = false;
                state.isFullViewerOpen = false;
              },
              false,
              actionName("openGeoPopup")
            ),

          openNonGeoPopup: () =>
            set(
              (state) => {
                state.popupType = "non-geo-entity";
                state.coords = null;
                state.isContentVisible = true;
                state.isContentCollapsed = false;
                state.isFullViewerOpen = false;
              },
              false,
              actionName("openNonGeoPopup")
            ),

          openMediaViewer: () =>
            set(
              (state) => {
                state.popupType = "media-viewer";
                state.coords = null;
                state.isContentVisible = true;
                state.isContentCollapsed = false;
                state.isFullViewerOpen = false;
              },
              false,
              actionName("openMediaViewer")
            ),

          openMediaViewerInMapMode: () =>
            set(
              (state) => {
                state.popupType = "media-viewer";
                state.coords = null;
                state.isContentVisible = false;
                state.isContentCollapsed = false;
                state.isFullViewerOpen = false;
              },
              false,
              actionName("openMediaViewerInMapMode")
            ),

          closePopup: () =>
            set(
              (state) => {
                state.popupType = null;
                state.coords = null;
                state.isContentVisible = true;
                state.isContentCollapsed = false;
                state.isFullViewerOpen = false;
              },
              false,
              actionName("closePopup")
            ),

          toggleContent: () =>
            set(
              (state) => {
                state.isContentVisible = !state.isContentVisible;
              },
              false,
              actionName("toggleContent")
            ),

          bumpGeoPopupLayoutRevision: () =>
            set(
              (state) => {
                state.geoPopupLayoutRevision += 1;
              },
              false,
              actionName("bumpGeoPopupLayoutRevision")
            ),

          openFullMediaViewer: () =>
            set(
              (state) => {
                state.popupType = "media-viewer";
                state.isFullViewerOpen = true;
              },
              false,
              actionName("openFullMediaViewer")
            ),

          closeFullMediaViewer: () =>
            set(
              (state) => {
                state.popupType = "geo-entity";
                state.isFullViewerOpen = false;
              },
              false,
              actionName("closeFullMediaViewer")
            ),

          reset: () => set(store.getInitialState(), false, actionName("reset"))
        }
      }))
    ),
    { name: "popupStore" }
  )
);

export const usePopupSelector = () =>
  usePopupStore(
    useShallow((state) => ({
      isOpen: state.popupType !== null, // backward-compat helper
      popupType: state.popupType,
      coords: state.coords,
      isContentVisible: state.isContentVisible,
      isContentCollapsed: state.isContentCollapsed,
      geoPopupLayoutRevision: state.geoPopupLayoutRevision,
      isFullViewerOpen: state.isFullViewerOpen
    }))
  );

export const usePopupActions = () => usePopupStore((state) => state.actions);
